package ai.jessie.web.theme

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.disabled
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonKind
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.ContentBuilder
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLAnchorElement

private const val PRESSED = "[aria-pressed=\"true\"]"
private const val CURRENT_PAGE = "[aria-current=\"page\"]"

val CalendarArrowStyle = CssStyle.base {
    css(
        "display" to "inline-flex",
        "align-items" to "center",
        "justify-content" to "center",
        "height" to "32px",
        "width" to "32px",
        "min-width" to "32px",
        "flex-shrink" to "0",
        "border-radius" to JessieTokens.RADIUS_CONTROL,
        "border" to "1px solid ${LineInputVar.value()}",
        "color" to InkVar.value(),
        "background" to SurfaceVar.value(),
    )
}

// Button: Silk's ButtonStyle re-skinned; the variants only set Silk's button colour variables.

@InitSilk
fun initJessieButton(ctx: InitSilkContext) {
    ctx.theme.replaceStyle(ButtonStyle) {
        base {
            css(
                "display" to "inline-flex",
                "align-items" to "center",
                "justify-content" to "center",
                "gap" to "8px",
                "height" to JessieTokens.CONTROL_HEIGHT,
                "padding" to "0 16px",
                "border-radius" to JessieTokens.RADIUS_CONTROL,
                "font-size" to "14px",
                "font-weight" to "600",
                "border" to "1px solid transparent",
                "cursor" to "pointer",
                "white-space" to "nowrap",
                "text-decoration" to "none",
                "box-sizing" to "border-box",
                "color" to ButtonVars.Color.value(),
                "background" to ButtonVars.BackgroundDefaultColor.value(),
            )
        }
        // Silk's Button lays its content out in a Row.
        cssRule(" > div") { css("gap" to "8px") }
        cssRule(":hover:not(:disabled):not([aria-disabled=\"true\"])") {
            css(
                "background" to ButtonVars.BackgroundHoverColor.value(),
                "color" to ButtonVars.Color.value(),
            )
        }
    }
}

private fun buttonColors(
    background: CSSColorValue,
    color: CSSColorValue,
    hover: CSSColorValue = background,
    borderColor: CSSColorValue? = null,
): Modifier =
    Modifier.setVariable(ButtonVars.BackgroundDefaultColor, background)
        .setVariable(ButtonVars.BackgroundHoverColor, hover)
        .setVariable(ButtonVars.Color, color)
        .then(borderColor?.let { css("border-color" to it) } ?: Modifier)

val PrimaryButtonVariant = ButtonStyle.addVariantBase {
    buttonColors(PrimaryVar.value(), Colors.White, hover = PrimaryHoverVar.value())
}

val TintButtonVariant = ButtonStyle.addVariantBase {
    buttonColors(PrimaryTintVar.value(), PrimaryVar.value())
}

val SecondaryButtonVariant = ButtonStyle.addVariantBase {
    buttonColors(
        SurfaceVar.value(),
        InkVar.value(),
        hover = SurfaceMutedVar.value(),
        borderColor = LineInputVar.value(),
    )
}

val GhostButtonVariant = ButtonStyle.addVariantBase {
    buttonColors(Colors.Transparent, Ink3Var.value())
}

val DarkButtonVariant = ButtonStyle.addVariantBase {
    buttonColors(InkVar.value(), GroundVar.value())
}

val DangerButtonVariant = ButtonStyle.addVariantBase {
    buttonColors(DangerTintVar.value(), DangerVar.value())
}

val SmallButtonStyle = CssStyle.base {
    css(
        "height" to JessieTokens.CONTROL_HEIGHT_SM,
        "padding" to "0 12px",
        "font-size" to "13px",
    )
}

/** Icon-only button: square at the control height. */
val IconButtonStyle = CssStyle.base {
    css("width" to JessieTokens.CONTROL_HEIGHT, "padding" to "0")
}

val SmallIconButtonStyle = SmallButtonStyle.extendedByBase {
    css("width" to JessieTokens.CONTROL_HEIGHT_SM, "padding" to "0")
}

/** Button variants from the style guide (`docs/design/components.md`, Button). */
enum class ButtonVariant {
    PRIMARY,
    TINT,
    SECONDARY,
    GHOST,
    DARK,
    DANGER,
}

internal fun ButtonVariant.toSilk(): CssStyleVariant<ButtonKind> =
    when (this) {
        ButtonVariant.PRIMARY -> PrimaryButtonVariant
        ButtonVariant.TINT -> TintButtonVariant
        ButtonVariant.SECONDARY -> SecondaryButtonVariant
        ButtonVariant.GHOST -> GhostButtonVariant
        ButtonVariant.DARK -> DarkButtonVariant
        ButtonVariant.DANGER -> DangerButtonVariant
    }

enum class ButtonSize {
    DEFAULT,
    SMALL,
}

// Badge

sealed interface BadgeKind : ComponentKind

val BadgeStyle =
    CssStyle.base<BadgeKind> {
        css(
            "display" to "inline-flex",
            "align-items" to "center",
            "height" to "24px",
            "padding" to "0 9px",
            "border-radius" to JessieTokens.RADIUS_PILL,
            "font-size" to "12px",
            "font-weight" to "600",
            "background" to SurfaceSunkenVar.value(),
            "color" to Ink3Var.value(),
        )
    }

val HoursBadgeVariant = BadgeStyle.addVariantBase {
    css("background" to HoursTintVar.value(), "color" to HoursVar.value())
}

val PrimaryBadgeVariant = BadgeStyle.addVariantBase {
    css("background" to PrimaryTintVar.value(), "color" to PrimaryVar.value())
}

val AccentBadgeVariant = BadgeStyle.addVariantBase {
    css("background" to AccentTintVar.value(), "color" to AccentVar.value())
}

val DangerBadgeVariant = BadgeStyle.addVariantBase {
    css("background" to DangerTintVar.value(), "color" to DangerVar.value())
}

val WarningBadgeVariant = BadgeStyle.addVariantBase {
    css("background" to WarningTintVar.value(), "color" to WarningVar.value())
}

/** Badge colour pairs: hours (green), primary, accent (violet), danger, warning, neutral. */
enum class BadgeTone {
    NEUTRAL,
    HOURS,
    PRIMARY,
    ACCENT,
    DANGER,
    WARNING,
}

private fun BadgeTone.toVariant(): CssStyleVariant<BadgeKind>? =
    when (this) {
        BadgeTone.NEUTRAL -> null
        BadgeTone.HOURS -> HoursBadgeVariant
        BadgeTone.PRIMARY -> PrimaryBadgeVariant
        BadgeTone.ACCENT -> AccentBadgeVariant
        BadgeTone.DANGER -> DangerBadgeVariant
        BadgeTone.WARNING -> WarningBadgeVariant
    }

// Chip, segmented control, tabs

val ChipStyle = CssStyle {
    base {
        css(
            "display" to "inline-flex",
            "align-items" to "center",
            "height" to "32px",
            "padding" to "0 12px",
            "border-radius" to JessieTokens.RADIUS_PILL,
            "border" to "1px solid ${LineInputVar.value()}",
            "background" to SurfaceVar.value(),
            "font-size" to "13px",
            "font-weight" to "500",
            "color" to Ink2Var.value(),
            "cursor" to "pointer",
        )
    }
    cssRule(PRESSED) {
        css(
            "border-color" to PrimaryVar.value(),
            "background" to PrimaryTintSoftVar.value(),
            "color" to PrimaryVar.value(),
        )
    }
}

val SegmentedStyle = CssStyle.base {
    css(
        "display" to "inline-flex",
        "align-self" to "flex-start",
        "background" to SurfaceSunkenVar.value(),
        "border-radius" to JessieTokens.RADIUS_CONTROL,
        "padding" to "3px",
        "gap" to "2px",
    )
}

val SegmentStyle = CssStyle {
    base {
        css(
            "padding" to "6px 12px",
            "border-radius" to "7px",
            "font-size" to "13px",
            "font-weight" to "600",
            "color" to Ink3Var.value(),
            "border" to "0",
            "background" to "transparent",
            "cursor" to "pointer",
        )
    }
    cssRule(PRESSED) {
        css(
            "background" to SurfaceVar.value(),
            "color" to InkVar.value(),
            "box-shadow" to JessieTokens.SHADOW_SEGMENT,
        )
    }
}

val TabsStyle = CssStyle.base {
    css("display" to "flex", "border-bottom" to "1px solid ${LineVar.value()}")
}

val TabStyle = CssStyle {
    base {
        css(
            "padding" to "12px 4px",
            "margin-right" to "20px",
            "font-size" to "14px",
            "font-weight" to "500",
            "color" to Ink3Var.value(),
            "border-bottom" to "2px solid transparent",
            "margin-bottom" to "-1px",
        )
    }
    cssRule(":hover") { css("color" to LinkHoverVar.value()) }
    cssRule(CURRENT_PAGE) {
        css(
            "color" to InkVar.value(),
            "border-color" to PrimaryVar.value(),
            "font-weight" to "600",
        )
    }
}

@Composable
fun themedButton(
    label: String,
    onClick: () -> Unit,
    variant: ButtonVariant = ButtonVariant.SECONDARY,
    size: ButtonSize = ButtonSize.DEFAULT,
    disabled: Boolean = false,
    submit: Boolean = false,
    elementId: String? = null,
    leading: (@Composable () -> Unit)? = null,
) {
    Button(
        onClick = { onClick() },
        modifier =
            Modifier.thenIf(size == ButtonSize.SMALL) { SmallButtonStyle.toModifier() }
                .then(elementId?.let { Modifier.id(it) } ?: Modifier)
                // Silk only sets aria-disabled; the native attribute also blocks form submission.
                .thenIf(disabled) { Modifier.disabled() },
        variant = variant.toSilk(),
        type = if (submit) ButtonType.Submit else ButtonType.Button,
        enabled = !disabled,
    ) {
        leading?.invoke()
        Text(label)
    }
}

/** A navigation link styled as a button; use for page changes, [themedButton] for actions. */
@Composable
fun linkButton(
    label: String,
    href: String,
    variant: ButtonVariant = ButtonVariant.SECONDARY,
    size: ButtonSize = ButtonSize.DEFAULT,
    leading: ContentBuilder<HTMLAnchorElement>? = null,
) {
    A(
        href = href,
        attrs =
            ButtonStyle.toModifier(variant.toSilk())
                .thenIf(size == ButtonSize.SMALL) { SmallButtonStyle.toModifier() }
                .toAttrs(),
    ) {
        leading?.invoke(this)
        Text(label)
    }
}

@Composable
fun badge(label: String, tone: BadgeTone = BadgeTone.NEUTRAL) {
    Span(attrs = BadgeStyle.toModifier(tone.toVariant()).toAttrs()) { Text(label) }
}

/** A number in Dank Mono with tabular figures; [unit] stays in the UI face. */
@Composable
fun number(value: String, unit: String? = null) {
    Span(attrs = NumStyle.toAttrs()) { Text(value) }
    if (unit != null) Span(attrs = KpiUnitStyle.toAttrs()) { Text(" $unit") }
}
